import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const headingFont = "'Plus Jakarta Sans', sans-serif";

/* ─── Page container ─── */
export const Container = styled(Box)({
  padding: '12px 0 48px',
  textAlign: 'left',
});

/* ─── Intro panel: heading, search and filters ─── */
export const HeaderBox = styled('section')({
  marginBottom: 24,
  padding: '44px 48px 36px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  '@media (max-width: 900px)': {
    padding: '34px 28px 28px',
  },
  '@media (max-width: 600px)': {
    borderRadius: 18,
    padding: '26px 16px 18px',
    marginBottom: 16,
  },
});

export const Eyebrow = styled('p')({
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
});

export const HeadingText = styled('h1')({
  fontFamily: headingFont,
  fontSize: '2.6rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 12px',
  '@media (max-width: 900px)': {
    fontSize: '2.2rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
});

export const InfoText = styled('p')({
  fontSize: '1.1rem',
  lineHeight: 1.55,
  color: muted,
  margin: 0,
  maxWidth: 620,
  '@media (max-width: 600px)': {
    fontSize: '1rem',
  },
});

/* ─── Search + button row ─── */
export const SearchRow = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  gap: 12,
  maxWidth: 760,
  margin: '24px 0 16px',
  '@media (max-width: 600px)': {
    flexDirection: 'column',
    gap: 10,
    margin: '18px 0 12px',
  },
});

export const SearchField = styled('label')({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 14px',
  minHeight: 48,
  borderRadius: 12,
  background: '#ffffff',
  border: '1.5px solid rgba(26, 26, 46, 0.1)',
  boxShadow: '0 2px 10px rgba(26, 26, 46, 0.04)',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  cursor: 'text',
  '& svg': { color: muted, fontSize: 22 },
  '& input': {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: '1rem',
    color: ink,
    padding: '12px 0',
  },
  '& input::placeholder': { color: '#8a93a6' },
  '&:focus-within': {
    borderColor: blue,
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.12)',
  },
});

export const ServicesButton = styled('button')({
  padding: '0 22px',
  minHeight: 48,
  borderRadius: 10,
  border: `1.5px solid ${ink}`,
  background: '#ffffff',
  color: ink,
  fontFamily: 'inherit',
  fontSize: '0.96rem',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, color 0.15s ease',
  '&:hover, &:focus-visible': {
    borderColor: blue,
    color: blue,
  },
});

/* ─── Provider count ─── */
export const ProviderCountText = styled('p')({
  fontSize: '0.95rem',
  fontWeight: 600,
  color: ink,
  margin: '0 0 16px',
  '& span': {
    color: muted,
    fontWeight: 500,
  },
});

/* ─── No results ─── */
export const NoResultsBox = styled(Box)({
  margin: '32px 0',
  padding: '32px 24px',
  textAlign: 'center',
  borderRadius: 16,
  background: MINT_MIST,
  border: '1px dashed rgba(26, 26, 46, 0.14)',
  color: muted,
  fontSize: '1rem',
});

/* ─── Loader ─── */
export const LoaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
});

/* ─── Business card (also used on AccountPage) ─── */
export const BusinessCard = styled(Box)({
  height: '100%',
  borderRadius: 16,
  background: '#ffffff',
  transition: 'transform 0.15s ease',
  '& > .MuiCard-root': {
    borderRadius: '16px !important',
    border: '1px solid rgba(26, 26, 46, 0.07)',
    boxShadow: '0 2px 10px rgba(26, 26, 46, 0.04)',
  },
  '& > .MuiCard-root:hover': {
    borderColor: 'rgba(13, 78, 254, 0.3)',
    boxShadow: '0 10px 24px rgba(13, 78, 254, 0.1)',
  },
});

/* ─── Pagination ─── */
export const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 36,
  '& .MuiPaginationItem-root': {
    fontFamily: 'inherit',
    fontWeight: 600,
    borderRadius: 10,
    color: ink,
  },
  '& .MuiPaginationItem-root.Mui-selected': {
    color: '#ffffff',
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    boxShadow: '0 4px 12px rgba(13, 78, 254, 0.25)',
  },
  '& .MuiPaginationItem-root:hover:not(.Mui-selected)': {
    background: 'rgba(13, 78, 254, 0.06)',
    color: blue,
  },
});
